// components/DataGrid/DetailPanel/LoginHistoryTimeline.js
// Self-contained control: takes only a raw `logins` array (ISO strings) and
// does its own grouping/layout. Kept out of UserFunctionPanel.js so that
// panel stays a plain layout of sections, not the place login-timeline math
// lives.

import React, { useState } from 'react';
import { formatRelativeTime } from '../../../helpers/DateHelpers.js';
import './LoginHistoryTimeline.css';

const MAX_STORED_LOGINS = 10;
const DOT_MIN_SIZE = 10;
const DOT_MAX_SIZE = 28;
const MS_PER_DAY = 86400000;
const MS_PER_WEEK = 7 * MS_PER_DAY;

function dayKey(iso) {
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatExactTime(iso) {
  return new Date(iso).toLocaleString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

function formatDayLabel(day) {
  return new Date(`${day}T12:00:00`).toLocaleDateString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

function formatTickLabel(date) {
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

function formatCellDate(date) {
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatMonthLabel(date) {
  return date.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
}

// Mon-first weekday index (0=Mon..6=Sun) for the 1st of the month.
function isoWeekdayOfMonthStart(year, month) {
  const jsDay = new Date(year, month, 1).getDay(); // 0=Sun..6=Sat
  return (jsDay + 6) % 7;
}

const CALENDAR_MONTHS = 3;
const WEEKDAY_INITIALS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

export default function LoginHistoryTimeline({ logins }) {
  const [hoveredDay, setHoveredDay] = useState(null);

  const sorted = Array.isArray(logins)
    ? [...logins].filter(Boolean).sort((a, b) => new Date(b) - new Date(a))
    : [];

  if (sorted.length === 0) {
    return (
      <div className="lht-root">
        <div className="lht-empty">No logins recorded</div>
      </div>
    );
  }

  const byDay = {};
  sorted.forEach((iso) => {
    const key = dayKey(iso);
    (byDay[key] = byDay[key] || []).push(iso);
  });

  const days = Object.keys(byDay).sort();
  const oldestDay = days[0];
  const oldest = new Date(`${oldestDay}T00:00:00`);
  const now = new Date();
  const span = Math.max(now - oldest, MS_PER_DAY);
  const pct = (date) => Math.min(100, Math.max(0, ((date - oldest) / span) * 100));

  const maxCount = Math.max(...Object.values(byDay).map((arr) => arr.length));

  const points = days.map((day) => {
    const isos = byDay[day];
    const count = isos.length;
    const dayDate = new Date(`${day}T12:00:00`);
    const size = DOT_MIN_SIZE + Math.round((count / maxCount) * (DOT_MAX_SIZE - DOT_MIN_SIZE));
    return {
      day,
      leftPct: pct(dayDate),
      size,
      count,
      label: count > 1 ? `×${count}` : '',
      dateLabel: `${formatDayLabel(day)} · ${count} login${count === 1 ? '' : 's'}`,
      times: isos.map(formatExactTime),
    };
  });

  const ticks = [];
  for (let t = oldest.getTime(); t <= now.getTime(); t += MS_PER_WEEK) {
    const d = new Date(t);
    ticks.push({ key: d.toISOString(), leftPct: pct(d), label: formatTickLabel(d) });
  }

  const lastLoginIso = sorted[0];
  const busiest = points.reduce((max, p) => (p.count > max.count ? p : max), points[0]);

  // Recent-activity calendar: always the same fixed number of real calendar
  // months, regardless of how sparse or spread out the stored logins are —
  // some months may show no activity at all, which is expected.
  const calendarMonths = [];
  for (let m = CALENDAR_MONTHS - 1; m >= 0; m--) {
    const year = now.getFullYear();
    const month = now.getMonth() - m;
    const numDays = new Date(year, month + 1, 0).getDate();
    const leadingBlanks = isoWeekdayOfMonthStart(year, month);

    const cells = Array.from({ length: leadingBlanks }, () => null);
    for (let day = 1; day <= numDays; day++) {
      const cellDate = new Date(year, month, day);
      const key = dayKey(cellDate);
      const count = (byDay[key] || []).length;
      cells.push({
        key,
        count,
        isFuture: cellDate > now,
        label: `${formatCellDate(cellDate)}: ${count} login${count === 1 ? '' : 's'}`,
      });
    }

    calendarMonths.push({ key: `${year}-${month}`, label: formatMonthLabel(new Date(year, month, 1)), cells });
  }

  return (
    <div className="lht-root">
      <div className="lht-stats">
        <div className="lht-stat">
          <div className="lht-stat-value">{formatExactTime(lastLoginIso)}</div>
          <div className="lht-stat-label">Last login &middot; {formatRelativeTime(lastLoginIso)}</div>
        </div>
        <div className="lht-stat">
          <div className="lht-stat-value">{formatDayLabel(busiest.day)}</div>
          <div className="lht-stat-label">Busiest day ({busiest.count} login{busiest.count === 1 ? '' : 's'})</div>
        </div>
        <div className="lht-stat">
          <div className="lht-stat-value">Last {sorted.length} logins</div>
          <div className="lht-stat-label">Only the most recent {MAX_STORED_LOGINS} are stored</div>
        </div>
      </div>

      <div className="lht-track">
        {ticks.map((t) => (
          <React.Fragment key={t.key}>
            <div className="lht-gridline" style={{ '--lht-pos': `${t.leftPct}%` }} />
            <div className="lht-tick-label" style={{ '--lht-pos': `${t.leftPct}%` }}>{t.label}</div>
          </React.Fragment>
        ))}
        <div className="lht-baseline" />
        {points.map((p) => (
          <div
            key={p.day}
            className="lht-point"
            style={{ '--lht-pos': `${p.leftPct}%` }}
            onMouseEnter={() => setHoveredDay(p.day)}
            onMouseLeave={() => setHoveredDay(null)}
          >
            <div
              className={`lht-dot${p.count > 1 ? ' lht-dot--multi' : ''}`}
              style={{ '--lht-size': `${p.size}px` }}
            >
              {p.label}
            </div>
            {hoveredDay === p.day && (
              <div className="lht-card">
                <div className="lht-card-header">{p.dateLabel}</div>
                {p.times.map((time) => (
                  <div key={time} className="lht-card-time">{time}</div>
                ))}
              </div>
            )}
          </div>
        ))}
        <div className="lht-today" title="Today" />
      </div>

      <div className="lht-heatmap">
        <div className="lht-heatmap-header">
          <span className="lht-heatmap-title">Login heatmap</span>
          <span className="lht-heatmap-legend">
            Less
            {[0, 1, 2].map((level) => (
              <span key={level} className={`lht-heatmap-cell lht-heat-${level}`} />
            ))}
            More
          </span>
        </div>
        <div className="lht-heatmap-months">
          {calendarMonths.map((month) => (
            <div className="lht-heatmap-month" key={month.key}>
              <div className="lht-heatmap-month-label">{month.label}</div>
              <div className="lht-heatmap-weekdays">
                {WEEKDAY_INITIALS.map((w, i) => (
                  <span key={i}>{w}</span>
                ))}
              </div>
              <div className="lht-heatmap-grid">
                {month.cells.map((cell, i) =>
                  cell === null ? (
                    <div key={`blank-${i}`} className="lht-heatmap-cell lht-heatmap-cell--blank" />
                  ) : (
                    <div
                      key={cell.key}
                      className={`lht-heatmap-cell lht-heat-${Math.min(cell.count, 2)}${cell.isFuture ? ' lht-heatmap-cell--future' : ''}`}
                      title={cell.isFuture ? undefined : cell.label}
                    />
                  )
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
